import { useLocalization } from "@fluent/react"
import { useState } from "react"
import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined"
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined"
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled"
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk"
import PhonePausedIcon from "@mui/icons-material/PhonePaused"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { format, parseISO } from "date-fns"
import { Link } from "react-router-dom"
import type { MeetingMinute } from "../../project/mettingMinutes/meetingMinutesSlice"
import { NewMMTimer } from "./NewMMTimer"
import { usePackage } from "../../permissions/usePackage"

type NewMeetingMinutesHeaderProps = {
  meetingMinute: MeetingMinute
  attendeeCount: number
  saving: boolean
  onCloseMeeting: () => void
  onSuspendMeeting: () => void
  onResumeMeeting: () => void
}

export const NewMeetingMinutesHeader = ({
  meetingMinute, attendeeCount, saving, onCloseMeeting, onSuspendMeeting, onResumeMeeting,
}: NewMeetingMinutesHeaderProps) => {
  const { l10n } = useLocalization()
  const tr = (key: string, fallback: string) => l10n.getString(key, null, `_${fallback}`)
  const theme = useTheme()
  const canWrite = usePackage("MeettingMinutes", "ReadWrite")
  const [size, setSize] = useState<"minimal" | "large">("large")
  const minimal = size === "minimal"
  const sizeToggleLabel = minimal ? tr("meeting-header-expand", "Pełny widok") : tr("meeting-header-collapse", "Widok minimalny")
  const neutralSurface = alpha(theme.palette.text.primary, 0.035)
  const lastMeeting = meetingMinute.meetings.find(meeting => meeting.id === meetingMinute.lastMeetingId)
  const lastMeetingStartDate = lastMeeting ? format(parseISO(lastMeeting.startDate as string), "yyyy-MM-dd HH:mm") : ""
  const lastMeetingEndDate = lastMeeting?.endDate ? format(parseISO(lastMeeting.endDate as string), "yyyy-MM-dd HH:mm") : ""
  const statusLabel = meetingMinute.status === "INMEETING"
    ? tr("operational-dashboard-meeting-in-progress", "W trakcie spotkania")
    : meetingMinute.status === "SUSPENDED"
      ? tr("operational-dashboard-meeting-suspended", "Spotkanie zawieszone")
      : meetingMinute.status === "CLOSED"
        ? tr("operational-dashboard-meeting-closed", "Spotkanie zakończone")
        : tr("operational-dashboard-meeting-new", "Spotkanie nierozpoczęte")

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: minimal ? 1.5 : { xs: 2, md: 3 } }}>
      <Stack direction={minimal ? "row" : { xs: "column", lg: "row" }} useFlexGap flexWrap="wrap"
        gap={minimal ? 1.5 : 3} justifyContent="space-between" alignItems="center">
        <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
          <Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" gap={1.5} sx={{ mb: minimal ? 0 : 1 }}>
            <Typography component="h1" variant={minimal ? "subtitle1" : "h5"} sx={{ fontWeight: 600, letterSpacing: -0.4, overflowWrap: "anywhere" }}>
              {meetingMinute.title}
            </Typography>
          </Stack>
          {lastMeeting?.name && <Typography variant={minimal ? "caption" : "body2"}
            sx={{ display: "block", mb: minimal ? 0 : 1.5, overflowWrap: "anywhere" }}>{lastMeeting.name}</Typography>}
          {!minimal && <>
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={2} sx={{ color: "text.secondary" }}>
            <Stack direction="row" spacing={0.75} alignItems="center"><CalendarMonthOutlinedIcon sx={{ fontSize: 17 }} /><Typography variant="caption">{lastMeetingStartDate || "—"}</Typography></Stack>
            <Stack direction="row" spacing={0.75} alignItems="center"><PersonOutlineOutlinedIcon sx={{ fontSize: 17 }} /><Typography variant="caption">{lastMeeting?.createdByFullName || meetingMinute.createdByFullName || "—"}</Typography></Stack>
            <Stack direction="row" spacing={0.75} alignItems="center"><PlaceOutlinedIcon sx={{ fontSize: 17 }} /><Typography variant="caption">{lastMeeting?.place || "—"}</Typography></Stack>
            <Stack direction="row" spacing={0.75} alignItems="center"><GroupsOutlinedIcon sx={{ fontSize: 17 }} /><Typography variant="caption">{tr("meeting-minutes-attendees", "Uczestnicy")}: {attendeeCount}</Typography></Stack>
          </Stack>
          </>}
        </Box>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={minimal ? 1 : 2.5} alignItems="center" sx={{ minWidth: 0, maxWidth: "100%" }}>
          <Chip size="small" label={statusLabel}
            icon={<Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "currentColor" }} />}
            sx={{
              borderRadius: 1.5, fontWeight: 600,
              bgcolor: meetingMinute.status === "INMEETING" ? "primary.main"
                : meetingMinute.status === "SUSPENDED" ? alpha(theme.palette.warning.main, 0.14) : "action.hover",
              color: meetingMinute.status === "INMEETING" ? "primary.contrastText"
                : meetingMinute.status === "SUSPENDED"
                  ? theme.palette.mode === "dark" ? "warning.light" : "warning.dark" : "text.secondary",
              "& .MuiChip-icon": { color: "inherit", ml: 1 },
            }} />
          <Box sx={{ px: minimal ? 0 : 1.5, py: minimal ? 0 : 1, borderRadius: 2, bgcolor: minimal ? "transparent" : neutralSurface, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, minWidth: 0 }}>
            {!minimal && !(meetingMinute.status === "INMEETING" && lastMeeting?.startDate) && <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary" }}>
              <ScheduleOutlinedIcon sx={{ fontSize: 16 }} /><Typography variant="caption">{tr("meeting-refresh-time", "Czas spotkania")}</Typography>
            </Stack>}
            {meetingMinute.status === "INMEETING" && lastMeeting?.startDate
              ? <NewMMTimer compact={minimal} startDate={lastMeeting.startDate} plannedMinutes={lastMeeting.plannedMeetingTime} status={meetingMinute.status} />
              : !minimal && <Typography variant="caption" color="text.secondary">{lastMeetingEndDate}</Typography>}
          </Box>
          <Stack direction={minimal ? "row" : "column"} useFlexGap flexWrap="wrap" gap={1}
            sx={minimal ? { "& .MuiButton-root": { fontSize: "0.8125rem", py: 0.5, px: 1 } } : undefined}>
            {canWrite && meetingMinute.status === "INMEETING" && <>
              <Button disabled={saving} variant="outlined" color="error" onClick={onCloseMeeting} startIcon={<PhoneDisabledIcon />}>
                {tr("meeting-minutes-end-meeting", "Zakończ spotkanie")}
              </Button>
              <Button disabled={saving} variant="outlined" color="inherit" onClick={onSuspendMeeting} startIcon={<PhonePausedIcon />}>
                {tr("meeting-minutes-suspend-meeting", "Zawieś spotkanie")}
              </Button>
            </>}
            {canWrite && (meetingMinute.status === "NEW" || meetingMinute.status === "CLOSED") && <Button variant="contained" component={Link}
              to={`/operational-dashboards/meeting/create/${encodeURIComponent(meetingMinute.id)}`} startIcon={<PhoneInTalkIcon />}>
              {tr("meeting-minutes-start-meeting", "Rozpocznij spotkanie")}
            </Button>}
            {canWrite && meetingMinute.status === "SUSPENDED" && <Button disabled={saving} variant="contained" onClick={onResumeMeeting} startIcon={<PhoneInTalkIcon />}>
              {tr("meeting-minutes-resume-meeting", "Przywróć spotkanie")}
            </Button>}
          </Stack>
          <Tooltip title={sizeToggleLabel}>
            <IconButton size="small" aria-label={sizeToggleLabel} aria-expanded={!minimal}
              onClick={() => setSize(minimal ? "large" : "minimal")}
              sx={{ color: "text.secondary", transform: minimal ? "none" : "rotate(180deg)", transition: "transform 200ms" }}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  )
}
